import { z } from "zod"

import {
  platformCommandEnvelopeV1Schema,
} from "./platform-command-v1.js"
import {
  correlationIdSchema,
  platformContextIdSchema,
  platformOperatorIdSchema,
  platformSessionIdSchema,
  tenantIdSchema,
} from "./identifiers.js"

const dnsNameSchema = z
  .string()
  .min(3)
  .max(253)
  .regex(
    /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/,
  )

export const tenantProvisioningCheckpointV1Schema = z.enum([
  "reserved",
  "site_created",
  "tenant_configured",
  "apps_installed",
  "migrated",
  "healthy",
])

export const tenantProvisioningStateV1Schema = z.enum([
  "reserved",
  "provisioning",
  "failed",
  "ready",
])

export const tenantProvisioningFailureCodeV1Schema = z.enum([
  "SITE_OPERATION_FAILED",
  "TENANT_IDENTITY_MISMATCH",
  "APP_VERSION_MISMATCH",
  "SCHEMA_VERSION_MISMATCH",
  "HEALTH_CHECK_FAILED",
])

export const tenantProvisioningReadinessV1Schema = z
  .object({
    siteExists: z.boolean(),
    configuredTenantMatches: z.boolean(),
    registryMatches: z.boolean(),
    requiredAppsMatch: z.boolean(),
    observedAppVersion: z.string().min(1).max(64).nullable(),
    expectedAppVersion: z.string().min(1).max(64),
    observedSchemaVersion: z.string().min(1).max(64).nullable(),
    expectedSchemaVersion: z.string().min(1).max(64),
    health: z.enum(["unknown", "degraded", "ready"]),
  })
  .strict()

export const tenantProvisioningSnapshotV1Schema = z
  .object({
    contractVersion: z.literal(1),
    targetTenantId: tenantIdSchema,
    hostname: dnsNameSchema,
    siteName: dnsNameSchema,
    state: tenantProvisioningStateV1Schema,
    checkpoint: tenantProvisioningCheckpointV1Schema,
    failureCode: tenantProvisioningFailureCodeV1Schema.nullable(),
    attempt: z.number().int().positive().safe(),
    revision: z.number().int().positive().safe(),
    readiness: tenantProvisioningReadinessV1Schema,
    publishedAt: z.string().datetime({ offset: true }).nullable(),
    updatedAt: z.string().datetime({ offset: true }),
  })
  .strict()
  .superRefine((snapshot, context) => {
    const publishable =
      snapshot.checkpoint === "healthy"
      && snapshot.readiness.siteExists
      && snapshot.readiness.configuredTenantMatches
      && snapshot.readiness.registryMatches
      && snapshot.readiness.requiredAppsMatch
      && snapshot.readiness.observedAppVersion
        === snapshot.readiness.expectedAppVersion
      && snapshot.readiness.observedSchemaVersion
        === snapshot.readiness.expectedSchemaVersion
      && snapshot.readiness.health === "ready"
    if (
      snapshot.state === "ready"
      && (!publishable || snapshot.publishedAt === null)
    ) {
      context.addIssue({
        code: "custom",
        message: "A ready route requires every authoritative readiness gate.",
      })
    }
    if (snapshot.state !== "ready" && snapshot.publishedAt !== null) {
      context.addIssue({
        code: "custom",
        path: ["publishedAt"],
        message: "Only a ready route may be published.",
      })
    }
  })

export const tenantProvisioningCommandV1Schema = z
  .object({
    envelope: platformCommandEnvelopeV1Schema,
    request: z
      .object({
        targetTenantId: tenantIdSchema,
        hostname: dnsNameSchema,
        siteName: dnsNameSchema,
      })
      .strict(),
  })
  .strict()
  .superRefine((command, context) => {
    const target = command.envelope.target
    if (
      target.scope !== "tenant"
      || target.targetTenantId !== command.request.targetTenantId
      || String(target.targetId) !== command.request.targetTenantId
      || target.targetType !== "tenant_route"
      || command.envelope.idempotencyNamespace !== "tenant.provision"
      || !["StartTenantProvisioning", "ResumeTenantProvisioning"].includes(
        command.envelope.commandType,
      )
      || command.envelope.payload.targetTenantId
        !== command.request.targetTenantId
      || command.envelope.payload.hostname !== command.request.hostname
      || command.envelope.payload.siteName !== command.request.siteName
    ) {
      context.addIssue({
        code: "custom",
        message: "Provisioning command context does not match its exact Tenant target.",
      })
    }
  })

export const tenantProvisioningResponseV1Schema = z.discriminatedUnion("ok", [
  z
    .object({
      responseVersion: z.literal(1),
      ok: z.literal(true),
      replayed: z.boolean(),
      snapshot: tenantProvisioningSnapshotV1Schema,
    })
    .strict(),
  z
    .object({
      responseVersion: z.literal(1),
      ok: z.literal(false),
      error: z
        .object({
          code: z.enum([
            "INVALID_REQUEST",
            "DENIED",
            "NOT_FOUND",
            "CONFLICT",
            "BUSY",
            "VERSION_CONFLICT",
            "PROVISIONING_FAILED",
            "UNAVAILABLE",
          ]),
          retryable: z.boolean(),
          message: z.string().min(1).max(128),
        })
        .strict(),
      current: tenantProvisioningSnapshotV1Schema.optional(),
    })
    .strict(),
])

export const tenantProvisioningQueryV1Schema = z
  .object({
    envelope: z
      .object({
        contractVersion: z.literal(1),
        platformContextId: platformContextIdSchema,
        platformOperatorId: platformOperatorIdSchema,
        platformSessionId: platformSessionIdSchema,
        correlationId: correlationIdSchema,
        queryType: z.literal("TenantProvisioning"),
        queryVersion: z.literal(1),
      })
      .strict(),
    targetTenantId: tenantIdSchema,
  })
  .strict()

export const tenantProvisioningCheckpointObservationV1Schema = z
  .object({
    checkpoint: tenantProvisioningCheckpointV1Schema,
    state: z.enum(["started", "completed", "failed"]),
    failureCode: tenantProvisioningFailureCodeV1Schema.nullable(),
    attempt: z.number().int().positive().safe(),
    routeRevision: z.number().int().positive().safe(),
    occurredAt: z.string().datetime({ offset: true }),
  })
  .strict()

export const tenantProvisioningWorkspaceV1Schema = z
  .object({
    responseVersion: z.literal(1),
    targetTenantId: tenantIdSchema,
    snapshot: tenantProvisioningSnapshotV1Schema.nullable(),
    checkpoints: z
      .array(tenantProvisioningCheckpointObservationV1Schema)
      .max(50),
  })
  .strict()

export type TenantProvisioningCheckpointV1 = z.infer<
  typeof tenantProvisioningCheckpointV1Schema
>
export type TenantProvisioningCommandV1 = z.infer<
  typeof tenantProvisioningCommandV1Schema
>
export type TenantProvisioningFailureCodeV1 = z.infer<
  typeof tenantProvisioningFailureCodeV1Schema
>
export type TenantProvisioningReadinessV1 = z.infer<
  typeof tenantProvisioningReadinessV1Schema
>
export type TenantProvisioningQueryV1 = z.infer<
  typeof tenantProvisioningQueryV1Schema
>
export type TenantProvisioningResponseV1 = z.infer<
  typeof tenantProvisioningResponseV1Schema
>
export type TenantProvisioningSnapshotV1 = z.infer<
  typeof tenantProvisioningSnapshotV1Schema
>
export type TenantProvisioningWorkspaceV1 = z.infer<
  typeof tenantProvisioningWorkspaceV1Schema
>
